import {
    Alert,
    Button,
    Select,
    MenuItem,
    SelectChangeEvent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import React from 'react'
import styled from 'styled-components'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {AssistantApi} from '../../services/AssistantApi.service'
import {HistoryContext} from '../../contexts/history'

type SupportTicketCategory =
    | 'Technical Support'
    | 'Bug Reports'
    | 'Account Issues'
    | 'Billing'
    | 'Feature Request'
    | 'General Inquiry'
    | 'Other'

const categoryLabels: Record<SupportTicketCategory, string> = {
    'Technical Support': 'Suporte Técnico',
    'Bug Reports': 'Relatório de Erros',
    'Account Issues': 'Problemas na Conta',
    Billing: 'Faturamento',
    'Feature Request': 'Solicitação de Recurso',
    'General Inquiry': 'Consulta Geral',
    Other: 'Outro',
}

const Page = styled.div`
    height: 100vh;
    margin-top: 0;
    padding: 28px 40px;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-y: auto;
    background-color: #f6f8fa;
    background-repeat: no-repeat;
    background-position: right 370px;
    background-size: 800px;
    flex: 1;
`

const Container = styled.div`
    display: flex;
    flex-direction: row;
    gap: 20px;
`

const LeftColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    flex: 1;
`

const FormCard = styled.div`
    background-color: #ffffff;
    border: 1px solid #e1e4e8;
    border-radius: 6px;
    box-shadow: rgba(27, 31, 35, 0.04) 0px 1px 0px;
    padding: 16px;
    height: fit-content;
`

const ContactCard = styled.div`
    background-color: #ffffff;
    border: 1px solid #e1e4e8;
    border-radius: 6px;
    box-shadow: rgba(27, 31, 35, 0.04) 0px 1px 0px;
    padding: 16px;
    height: fit-content;
`

const SegestionsCard = styled.div`
    background-color: #ffffff;
    border: 1px solid #e1e4e8;
    border-radius: 6px;
    box-shadow: rgba(27, 31, 35, 0.04) 0px 1px 0px;
    padding: 16px;
    width: 463px;
`

const Title = styled.label`
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    font-size: 14px;
`

const ValidationMessage = styled.div`
    background-color: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 16px;
    color: #856404;
    font-size: 14px;
`

export default function Suport() {
    const [typeOfCall, setTypeOfCall] =
        React.useState<SupportTicketCategory>('Technical Support')
    const {user} = React.useContext(HistoryContext)
    const [email, setEmail] = React.useState(user?.email || '')
    const [subject, setSubject] = React.useState('')
    const [message, setMessage] = React.useState('')
    const [file, setFile] = React.useState<File | undefined>(undefined)
    const [showSuccessAlert, setShowSuccessAlert] = React.useState(false)
    const [showErrorAlert, setShowErrorAlert] = React.useState(false)

    const handleTypeOfCallChange = (
        event: SelectChangeEvent<SupportTicketCategory>
    ) => {
        setTypeOfCall(event.target.value as SupportTicketCategory)
    }

    const api = new AssistantApi()

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0]
        if (selectedFile) {
            setFile(selectedFile)
        }
    }

    const isFormValid = () => {
        return (
            email.trim() !== '' &&
            subject.trim() !== '' &&
            message.trim().length >= 10
        )
    }

    const getValidationMessages = () => {
        const messages: string[] = []

        if (email.trim() === '') {
            messages.push('• E-mail é obrigatório')
        }

        if (subject.trim() === '') {
            messages.push('• Assunto é obrigatório')
        }

        if (message.trim() === '') {
            messages.push('• Sua mensagem é obrigatória')
        } else if (message.trim().length < 10) {
            messages.push(
                `• Sua mensagem deve ter no mínimo 10 caracteres (${message.trim().length}/10)`
            )
        }

        return messages
    }

    const handleSendSupport = async () => {
        if (isFormValid()) {
            try {
                setShowSuccessAlert(false)
                setShowErrorAlert(false)

                await api.sendEmailSupport(
                    email,
                    typeOfCall,
                    subject,
                    message,
                    file
                )

                setFile(undefined)
                setSubject('')
                setMessage('')
                setShowSuccessAlert(true)
            } catch (error) {
                setShowSuccessAlert(false)
                setShowErrorAlert(true)
            }
        }
    }

    const Mock = [
        {
            title: 'Quando devo acionar o suporte?',
            content:
                'O suporte (suporte@licitoguru.com ou (21) 97905-0563) deve ser acionado em casos de falhas técnicas, problemas de acesso, erros persistentes ou dúvidas que não estejam contempladas neste FAQ.',
        },
        {
            title: 'A plataforma ficou lenta ou não respondeu. O que fazer?',
            content:
                'Recomendamos atualizar a página e verificar a conexão com a internet. Caso o problema persista, entre em contato com o suporte (suporte@licitoguru.com ou (21) 97905-0563)',
        },
        {
            title: 'Como relatar um problema técnico?',
            content:
                'Ao relatar um problema técnico pela plataforma, ou pelos canais de contato direto (suporte@licitoguru.com ou (21) 97905-0563), descreva, com a maior quantidade de detalhes possível, qual o problema enfrentado, qual o usuário (e-mail) utilizado no sistema, e a data/horário da falha ocorrida.',
        },
        {
            title: 'O que significa a mensagem "Seus créditos esgotaram, para continuar usando entre em contato com um consultor"?',
            content:
                'Essa mensagem significa que o usuário utilizou todos os créditos disponíveis para aquele mês. Se você acredita que houve um erro, por favor, entre em contato com o nosso suporte: suporte@licitoguru.com ou (21) 97905-0563',
        },
        {
            title: 'Qual é o prazo de resposta do suporte?',
            content:
                'Nosso time de suporte prioriza a agilidade no atendimento. Em regra, as respostas são imediatas. Para a resolução de problemas técnicos, temos um prazo de 24 a 48 horas úteis para resolução completa, contudo, buscamos resolver a sua demanda o mais rápido possível.',
        },
        {
            title: 'Como funciona o sistema de créditos?',
            content:
                'No módulo de consultas, cada pergunta/mensagem enviada ao sistema corresponde a um crédito, independentemente se formulada dentro de um mesmo chat, ou em chats separados. No módulo de geração de documentos, cada documento gerado também corresponde a um crédito. Assim, se o usuário optar por gerar o Estudo Técnico Preliminar, um crédito será consumido, se optar por gerar o Estudo Técnico Preliminar e o Contrato, dois créditos serão consumidos, e assim sucessivamente. Os créditos são reabastecidos integralmente para o montante contratado no início de cada mês. Créditos não utilizados no mês anterior não são acumulados.',
        },
        {
            title: 'O que é a licito.guru?',
            content:
                'A licito.guru é uma plataforma de inteligência artificial voltada para licitações e contratos administrativos, desenvolvida para auxiliar os usuários na elaboração, revisão e estruturação dos documentos do processo licitatório, bem como na resposta a consultas sobre questões práticas e teóricas e apresentação de entendimentos e jurisprudência atualizada.',
        },
        {
            title: 'Quais as ferramentas disponíveis na licito.guru?',
            content:
                'Atualmente, a licito.guru possui dois módulos. No módulo de consultas o usuário pode interagir com a IA, formulando perguntas sobre questões práticas e teóricas, e solicitando apoio na análise, revisão e elaboração de minutas de documentos, por exemplo. No módulo de geração de documentos o usuário elabora documentos do processo licitatório a partir do preenchimento prévio de um formulário, com posterior geração do arquivo em formato editável (Word).',
        },
        {
            title: 'Quais documentos a ferramenta é capaz de gerar?',
            content:
                'Atualmente, é possível gerar Documento de Formalização da Demanda (DFD), Estudo Técnico Preliminar, Termo de Referência, Contrato, Edital, Ata de Registro de Preços e Matriz de Riscos.',
        },
        {
            title: 'É possível editar os documentos após serem gerados?',
            content:
                'Sim. Os arquivos criados no módulo de geração de documentos são disponibilizados em formato Word, permitindo sua livre edição. Ressaltamos ser imprescindível a revisão do conteúdo gerado, a fim de realizar os ajustes necessários, adequá-lo ao caso concreto e à realidade do órgão, bem como verificar a correção e a pertinência das disposições constantes do texto.',
        },
        {
            title: 'Os documentos gerados ficam salvos em minha conta?',
            content:
                'Sim, tanto as consultas formuladas como os documentos gerados ficam salvos e vinculados ao seu login, sendo possível arquiva-los e desarquiva-los a qualquer momento. Ressaltamos que qualquer um que utilize suas credenciais (login e senha) poderá ter acesso a essas informações, sendo de responsabilidade do usuário criar senhas fortes e não compartilha-la com terceiros não autorizados.',
        },
        {
            title: 'Meus documentos ficam armazenados com segurança?',
            content:
                'Sim. Os dados e documentos enviados são tratados de forma segura, respeitando boas práticas de proteção da informação.',
        },
        {
            title: 'Há um limite de tamanho para os arquivos anexados no Módulo de Consulta?',
            content:
                'Sim. Os arquivos enviados devem ter, no máximo, 20 MB cada.',
        },
    ]

    return (
        <Page>
            <Typography variant="h5" sx={{fontWeight: 700, color: '#1f2b3a', mb: 0.5}}>
                Suporte
            </Typography>
            <Typography variant="body2" sx={{color: '#5b6b7b', mb: 3}}>
                Abra um chamado, consulte as perguntas frequentes ou entre em contato com a equipe.
            </Typography>
            <Container>
                <LeftColumn>
                    <FormCard>
                        {showSuccessAlert && (
                            <Alert
                                severity="success"
                                onClose={() => setShowSuccessAlert(false)}
                                style={{marginBottom: '16px'}}
                            >
                                Seu chamado foi enviado com sucesso! Em breve,
                                nossa equipe de suporte entrará em contato com
                                você.
                            </Alert>
                        )}
                        {showErrorAlert && (
                            <Alert
                                severity="error"
                                onClose={() => setShowErrorAlert(false)}
                                style={{marginBottom: '16px'}}
                            >
                                Não foi possível enviar o chamado agora. Tente
                                novamente em instantes.
                            </Alert>
                        )}
                        <h3
                            style={{
                                fontFamily: 'Inter',
                                fontWeight: 500,
                                fontStyle: 'normal',
                                fontSize: '18px',
                                lineHeight: '100%',
                                letterSpacing: '0%',
                                margin: 0,
                                marginBottom: '16px',
                            }}
                        >
                            Abrir Chamado
                        </h3>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '16px',
                                marginBottom: '16px',
                            }}
                        >
                            <div style={{flex: 1}}>
                                <Title>E-mail</Title>
                                <input
                                    readOnly ={!!user?.email}
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    style={{
                                        width: '100%',
                                        height: '40px',
                                        padding: '8px',
                                        border: '1px solid #e1e4e8',
                                        borderRadius: '6px',
                                    }}
                                />
                            </div>
                            <div style={{flex: 1}}>
                                <Title>Tipo do chamado</Title>
                                <Select
                                    style={{
                                        width: '100%',
                                        height: '40px',
                                        border: '1px solid #e1e4e8',
                                        borderRadius: '6px',
                                    }}
                                    id="typeOfCall"
                                    labelId="typeOfCallLabel"
                                    value={typeOfCall}
                                    onChange={handleTypeOfCallChange}
                                >
                                    <MenuItem value="Technical Support">
                                        {categoryLabels['Technical Support']}
                                    </MenuItem>
                                    <MenuItem value="Bug Reports">
                                        {categoryLabels['Bug Reports']}
                                    </MenuItem>
                                    <MenuItem value="Account Issues">
                                        {categoryLabels['Account Issues']}
                                    </MenuItem>
                                    <MenuItem value="Billing">
                                        {categoryLabels['Billing']}
                                    </MenuItem>
                                    <MenuItem value="Feature Request">
                                        {categoryLabels['Feature Request']}
                                    </MenuItem>
                                    <MenuItem value="General Inquiry">
                                        {categoryLabels['General Inquiry']}
                                    </MenuItem>
                                    <MenuItem value="Other">
                                        {categoryLabels['Other']}
                                    </MenuItem>
                                </Select>
                            </div>
                        </div>
                        <div style={{marginBottom: '16px'}}>
                            <Title>Assunto</Title>

                            <input
                                type="text"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                style={{
                                    width: '100%',
                                    height: '40px',
                                    padding: '8px',
                                    border: '1px solid #e1e4e8',
                                    borderRadius: '6px',
                                }}
                            />
                        </div>
                        <div style={{marginBottom: '16px'}}>
                            <Title>Sua mensagem</Title>
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                style={{
                                    width: '100%',
                                    height: '150px',
                                    padding: '8px',
                                    border: '1px solid #e1e4e8',
                                    borderRadius: '6px',
                                    resize: 'vertical',
                                }}
                            ></textarea>
                        </div>
                        <Title>Enviar arquivo</Title>

                        <div style={{marginTop: '16px'}}>
                            <input
                                type="file"
                                onChange={handleFileChange}
                                style={{display: 'none'}}
                                id="file-upload"
                            />
                            <Button
                                component="label"
                                htmlFor="file-upload"
                                style={{
                                    width: '100%',
                                }}
                                variant="outlined"
                            >
                                {' '}
                                <FileUploadIcon />
                                {file ? file.name : 'Enviar'}
                            </Button>
                        </div>
                        <p
                            style={{
                                fontSize: '12px',
                                color: '#6a737d',
                                margin: '4px 0 16px 0',
                            }}
                        >
                            Adicione uma captura de tela, gravação ou qualquer
                            outro arquivo que ajude a descrever o seu problema.
                        </p>
                        {!isFormValid() &&
                            getValidationMessages().length > 0 && (
                                <ValidationMessage>
                                    <strong>Faltam campos para enviar:</strong>
                                    <div style={{marginTop: '8px'}}>
                                        {getValidationMessages().map(
                                            (message, index) => (
                                                <div key={index}>{message}</div>
                                            )
                                        )}
                                    </div>
                                </ValidationMessage>
                            )}
                        <div style={{marginTop: '16px'}}>
                            <Button
                                onClick={handleSendSupport}
                                disabled={!isFormValid()}
                                style={{
                                    width: '100%',
                                }}
                                variant="contained"
                            >
                                Enviar chamado
                            </Button>
                        </div>
                    </FormCard>

                    <ContactCard>
                        <h3
                            style={{
                                fontFamily: 'Inter',
                                fontWeight: 500,
                                fontStyle: 'normal',
                                fontSize: '18px',
                                lineHeight: '100%',
                                letterSpacing: '0%',
                                margin: 0,
                                marginBottom: '16px',
                            }}
                        >
                            Contato de Suporte
                        </h3>
                        <div style={{marginBottom: '12px'}}>
                            <Title>E-mail</Title>
                            <p
                                style={{
                                    margin: 0,
                                    fontSize: '14px',
                                    color: '#24292e',
                                }}
                            >
                                suporte@licitoguru.com
                            </p>
                        </div>
                        <div style={{marginBottom: '12px'}}>
                            <Title>Telefone</Title>
                            <p
                                style={{
                                    margin: 0,
                                    fontSize: '14px',
                                    color: '#24292e',
                                }}
                            >
                                (21) 97905-0563
                            </p>
                        </div>

                        <div>
                            <a
                                href="https://wa.me/5521979050563"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    margin: 0,
                                    fontSize: '14px',
                                    color: '#24292e',
                                    textDecoration: 'none',
                                }}
                            >
                                <WhatsAppIcon
                                    style={{
                                        verticalAlign: 'middle',
                                        marginRight: '8px',
                                    }}
                                />
                                Whatsapp
                            </a>
                        </div>
                    </ContactCard>
                </LeftColumn>

                <SegestionsCard>
                    <h3
                        style={{
                            fontFamily: 'Inter',
                            fontWeight: 500,
                            fontStyle: 'normal',
                            fontSize: '18px',
                            lineHeight: '100%',
                            letterSpacing: '0%',
                            margin: 0,
                            marginBottom: '16px',
                        }}
                    >
                        Perguntas frequentes
                    </h3>
                    {Mock.map((item) => (
                        <Accordion
                            key={item.title}
                            sx={{
                                borderRadius: '16px',
                                marginBottom: '16px',
                                boxShadow: 'none',
                                border: 'none',
                                backgroundColor: 'white',
                                '&.Mui-expanded': {
                                    backgroundColor: '#E3F2FD',
                                },
                                '&:before': {
                                    display: 'none',
                                },
                                '&.MuiAccordion-root': {
                                    borderRadius: '16px',
                                },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ArrowDropDownIcon />}
                                aria-controls="panel1-content"
                                id="panel1-header"
                            >
                                <Typography component="span">
                                    {item.title}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>{item.content}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </SegestionsCard>
            </Container>
        </Page>
    )
}
